import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SellIcon from "@mui/icons-material/Sell";
import { getUnitsByProperty } from "../../api/unitApi";
import { startSale } from "../../api/salesApi";
import { getApiErrorMessage } from "../../utils/concurrency";

const EMPTY = {
  unitId: "",
  company_name: "",
  contact_name: "",
  email: "",
  phone: "",
  reference: "",
};

const unitLabel = (unit) => unit.unit_name || unit.unit_code || `Unit ${unit.id}`;

export default function StartSaleDialog({ open, propertyId, propertyName, onClose, onStarted }) {
  const [units, setUnits] = useState([]);
  const [unitsError, setUnitsError] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !propertyId) return undefined;
    let cancelled = false;
    setForm(EMPTY);
    setError("");
    setUnitsError("");
    getUnitsByProperty(propertyId)
      .then((payload) => {
        if (cancelled) return;
        const rows = Array.isArray(payload) ? payload : payload?.results || [];
        setUnits(rows);
        if (rows.length === 1) {
          setForm((current) => ({ ...current, unitId: String(rows[0].id) }));
        }
      })
      .catch(() => {
        if (!cancelled) setUnitsError("Units could not be loaded.");
      });
    return () => {
      cancelled = true;
    };
  }, [open, propertyId]);

  const setField = (key) => (event) => {
    setForm((current) => ({ ...current, [key]: event.target.value }));
  };

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      const sale = await startSale({
        property_id: propertyId,
        unit_id: Number(form.unitId),
        company_name: form.company_name,
        contact_name: form.contact_name,
        email: form.email,
        phone: form.phone,
        reference: form.reference,
      });
      onStarted(sale);
    } catch (err) {
      setError(getApiErrorMessage(err, "The sale could not be started."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Start a sale</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {propertyName
            ? `This starts the sale for a home at ${propertyName}.`
            : "This starts the sale for one home."}{" "}
          No emails are sent. The sale opens at Request received, and you send the pack request yourself.
        </Typography>
        {unitsError && (
          <Typography color="error" variant="body2">
            {unitsError}
          </Typography>
        )}
        <TextField
          select
          size="small"
          label="Unit"
          value={form.unitId}
          onChange={setField("unitId")}
          disabled={!units.length}
        >
          <MenuItem value="" disabled>
            Choose a unit
          </MenuItem>
          {units.map((unit) => (
            <MenuItem key={unit.id} value={String(unit.id)}>
              {unitLabel(unit)}
            </MenuItem>
          ))}
        </TextField>
        <TextField size="small" label="Company" required value={form.company_name} onChange={setField("company_name")} />
        <TextField size="small" label="Contact" value={form.contact_name} onChange={setField("contact_name")} />
        <TextField size="small" label="Email" required type="email" value={form.email} onChange={setField("email")} />
        <TextField size="small" label="Phone" value={form.phone} onChange={setField("phone")} />
        <TextField size="small" label="Reference" value={form.reference} onChange={setField("reference")} />
        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" startIcon={<CloseIcon />} onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<SellIcon />}
          onClick={submit}
          disabled={saving || !form.unitId || !form.company_name.trim() || !form.email.trim()}
        >
          Start sale
        </Button>
      </DialogActions>
    </Dialog>
  );
}
